import type { ReactNode } from 'react';
import type { PlannerV3Step } from '../model/v3-status';
import { pickCss } from '@/shared/lib/css-utils';
import { PLANNER_V3_STEPS, plannerStepState } from '../model/v3-status';
import css from './planner-v3-header.module.scss';

const clientCss = pickCss(css, css.client);
const pillCss = pickCss(css, css.pill);

type PlannerV3HeaderProps = {
  /** Page heading for assistive tech; the header shows no title. */
  heading?: string;
  /** Host links, e.g. `Work · Mini planner · From request`. */
  breadcrumb?: ReactNode;
  clientLabel: string;
  /** Re-opens the client brief; the chip is plain text without it. */
  onEditBrief?: () => void;
  /** Dates · travellers · notes. */
  context?: string | null;
  savedLabel?: string | null;
  step: PlannerV3Step;
  actions?: ReactNode;
  /** A line under the header row, e.g. why the primary action is blocked. */
  notice?: ReactNode;
};

export const PlannerV3Header = ({
  heading = 'Mini planner',
  breadcrumb,
  clientLabel,
  onEditBrief,
  context,
  savedLabel,
  step,
  actions,
  notice,
}: PlannerV3HeaderProps) => {
  const clientBody = (
    <>
      <span className={css.clientName}>{clientLabel}</span>
      {onEditBrief ? (
        <span className={css.clientEdit}>✎ edit brief</span>
      ) : null}
    </>
  );
  return (
    <header className={css.root}>
      <h1 className={css.srOnly}>{heading}</h1>
      <div className={css.lead}>
        {breadcrumb ? <nav className={css.breadcrumb}>{breadcrumb}</nav> : null}
        <div className={css.subject}>
          {onEditBrief ? (
            <button
              aria-label={`${clientLabel} — edit brief`}
              className={clientCss({ isButton: true })}
              onClick={onEditBrief}
              type="button"
            >
              {clientBody}
            </button>
          ) : (
            <span className={clientCss()}>{clientBody}</span>
          )}
          {context ? (
            <span className={css.context} title={context}>
              {context}
            </span>
          ) : null}
          {savedLabel ? <span className={css.saved}>{savedLabel}</span> : null}
        </div>
      </div>
      <ol aria-label="Proposal status" className={css.steps}>
        {PLANNER_V3_STEPS.map((entry) => {
          const state = plannerStepState(entry.key, step);
          return (
            <li className={css.step} key={entry.key}>
              <span
                aria-current={state === 'current' ? 'step' : undefined}
                className={pillCss({ state })}
              >
                {entry.label}
              </span>
            </li>
          );
        })}
      </ol>
      {actions ? <div className={css.actions}>{actions}</div> : null}
      {notice ? <div className={css.notice}>{notice}</div> : null}
    </header>
  );
};
